import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  API_BASE_URL,
  respondToInvitation,
  getUserType,
  getUserId,
  getCreatorPaymentDetails,
} from '../api/api';
import './InvitationAction.css';

function InvitationAction() {
  const { action } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const invitationId = searchParams.get('id');
  const campaignNameFromUrl = searchParams.get('campaign');

  /** Same dashboard behavior: email → web → login → authenticated /invitation/respond (sets in_progress, full task, etc.) */
  const loginAsCreatorUrl = useCallback(() => {
    const returnTo = `${location.pathname}${location.search}`;
    return `/login?type=creator&redirect=${encodeURIComponent(returnTo)}`;
  }, [location.pathname, location.search]);

  const isCreatorSession = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(localStorage.getItem('token')) && getUserType() === 'creator';
  }, []);
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (invitationId) {
      fetchInvitationDetails();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [invitationId]);

  const fetchInvitationDetails = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/campaigns/invitation/${invitationId}/details`
      );
      const data = await response.json();
      
      if (data.success) {
        setInvitation(data);
      } else {
        setError(data.detail || 'Failed to load invitation details');
      }
    } catch (err) {
      setError('Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    setProcessing(true);
    setError(null);

    if (!isCreatorSession()) {
      navigate(loginAsCreatorUrl());
      setProcessing(false);
      return;
    }

    const ut = getUserType();
    if (ut && ut !== 'creator') {
      setError(
        'Sign in with your creator account (the email this invitation was sent to).'
      );
      setProcessing(false);
      return;
    }

    const apiAction = action === 'accept' ? 'accept' : 'reject';

    try {
      if (apiAction === 'accept') {
        const creatorId = getUserId();
        if (creatorId) {
          const payRes = await getCreatorPaymentDetails(creatorId);
          if (!payRes.data?.has_payment_details) {
            setError(
              'Add payout details before accepting. Open Payment Details in your creator dashboard, then return here.'
            );
            setProcessing(false);
            return;
          }
        }
      }

      await respondToInvitation(invitationId, apiAction);

      setResult({
        success: true,
        action: action,
        message:
          action === 'accept'
            ? 'You have successfully accepted the invitation!'
            : 'You have declined the invitation.',
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail && typeof detail === 'object'
            ? JSON.stringify(detail)
            : err.message || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="invitation-action-page">
          <div className="invitation-card loading">
            <div className="spinner"></div>
            <p>Loading invitation details...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (result) {
    return (
      <>
        <Navbar />
        <div className="invitation-action-page">
          <div className={`invitation-card result ${result.action === 'accept' ? 'success' : 'declined'}`}>
            <div className="result-icon">
              {result.action === 'accept' ? '✅' : '👋'}
            </div>
            <h1>{result.action === 'accept' ? 'Invitation Accepted!' : 'Invitation Declined'}</h1>
            <p className="result-message">{result.message}</p>
            
            {result.action === 'accept' && (
              <div className="next-steps">
                <h3>What's Next?</h3>
                <ul>
                  <li>📋 Review the complete campaign brief in your dashboard</li>
                  <li>🎨 Create amazing content following the guidelines</li>
                  <li>📤 Submit your content for brand approval</li>
                  <li>💰 Get paid after content goes live!</li>
                </ul>
              </div>
            )}
            
            {result.action === 'reject' && (
              <div className="declined-note">
                <p>No worries! New opportunities come up all the time.</p>
                <p>Keep your profile updated to receive more invitations.</p>
              </div>
            )}
            
            <div className="result-actions">
              <a href="/creator-dashboard" className="btn-primary">
                Open Creator Dashboard
              </a>
              <a href="/" className="btn-secondary">
                Back to Home
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error && !invitation) {
    return (
      <>
        <Navbar />
        <div className="invitation-action-page">
          <div className="invitation-card error">
            <div className="error-icon">⚠️</div>
            <h1>Oops!</h1>
            <p>{error}</p>
            <a href="/" className="btn-primary">Back to Home</a>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const isAccept = action === 'accept';

  return (
    <>
      <Navbar />
      <div className="invitation-action-page">
        <div className="invitation-card">
          <div className="card-header">
            <span className="action-badge">{isAccept ? '✨ Accept Invitation' : '👋 Decline Invitation'}</span>
            <h1>{invitation?.campaign?.name || campaignNameFromUrl || 'Campaign Invitation'}</h1>
            <p className="brand-name">by {invitation?.brand?.name || 'Brand'}</p>
          </div>

          <div className="card-body">
            {invitation?.invitation?.status !== 'pending' && invitation?.invitation?.status !== 'on_standby' ? (
              <div className="already-responded">
                <span className="status-icon">ℹ️</span>
                <p>This invitation has already been {invitation?.invitation?.status}.</p>
              </div>
            ) : (
              <>
                <div className="payment-summary">
                  <h3>💰 Your Earnings</h3>
                  <div className="payment-breakdown">
                    <div className="payment-item">
                      <span>Fixed Payment (60%)</span>
                      <strong>{formatCurrency(invitation?.invitation?.fixed_payment)}</strong>
                    </div>
                    <div className="payment-item">
                      <span>Performance Bonus (40%)</span>
                      <strong>Up to {formatCurrency(invitation?.invitation?.performance_payment)}</strong>
                    </div>
                    <div className="payment-total">
                      <span>Total Potential</span>
                      <strong>{formatCurrency(invitation?.invitation?.base_payment)}</strong>
                    </div>
                  </div>
                </div>

                {invitation?.campaign?.content_types?.length > 0 && (
                  <div className="campaign-details">
                    <div className="detail-item">
                      <span className="label">Content Type</span>
                      <span className="value">{invitation.campaign.content_types.join(', ')}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Deadline</span>
                      <span className="value">{invitation.campaign.deadline_days} days</span>
                    </div>
                  </div>
                )}

                {invitation?.campaign?.content_brief_preview && (
                  <div className="brief-preview">
                    <h4>📋 Brief Preview</h4>
                    <p>{invitation.campaign.content_brief_preview}</p>
                  </div>
                )}

                <div className="confirmation-section">
                  {!isCreatorSession() && (
                    <div className="login-hint" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0f7ff', borderRadius: '8px', fontSize: '0.95rem' }}>
                      Sign in with your <strong>creator</strong> account to accept or decline — same flow as the dashboard (campaign status updates correctly).
                      <div style={{ marginTop: '0.5rem' }}>
                        <a href={loginAsCreatorUrl()} className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                          Sign in to respond
                        </a>
                      </div>
                    </div>
                  )}
                  <p className="confirm-text">
                    {isAccept 
                      ? 'By accepting, you agree to create content according to the campaign guidelines and meet the deadline.'
                      : 'Are you sure you want to decline this invitation? This action cannot be undone.'}
                  </p>
                  
                  {error && <div className="error-message">{error}</div>}
                  
                  <div className="action-buttons">
                    <button 
                      className={`btn-action ${isAccept ? 'accept' : 'decline'}`}
                      onClick={handleAction}
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <span className="spinner-small"></span>
                          Processing...
                        </>
                      ) : !isCreatorSession() ? (
                        isAccept ? '✅ Sign in to accept' : '👋 Sign in to decline'
                      ) : isAccept ? (
                        '✅ Accept Invitation'
                      ) : (
                        '👋 Decline Invitation'
                      )}
                    </button>
                    
                    {isAccept ? (
                      <a href={`/invitation/reject?id=${invitationId}`} className="btn-secondary">
                        Not interested
                      </a>
                    ) : (
                      <a href={`/invitation/accept?id=${invitationId}`} className="btn-secondary">
                        Wait, I want to accept
                      </a>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default InvitationAction;
